import { action } from 'mobx';
import { TTrackFunction } from '~/common/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import { TAnalyticsEventAction } from '~/services/analytics/analytics.types';
import type { RootStore } from '~/stores/root.store';
import {
    BillingModalType,
    IBillingFieldError,
    IRecurlyPurchasePreviewResponse
} from '../types/membership.types';
import { PollingSubscription } from '~/services/polling-subscription.service';
import {
    getDiscountAmountWithUnit,
    getReferrerFullName
} from '~/components/Membership/utils/membership.utils';
import { TModalStore } from '~/providers/modal.provider';
import { ClientPendingTaskType } from '~/common/hooks/ClientPendingTask/client-pending-task.types';
import { LoggerService } from '~/services/logger/logger.service';
import { trackError } from '~/services/logger/error.utils';
import { deleteStoredCouponCode } from '~/utils/coupons.utils';
import { IIntercomService } from '~/types/angular.types';
import type { ForceFetchUserFn } from '~/common/hooks/useForceFetchUser';
import type { IUserResponse } from '~/types/server.types';
import { isReactFirst } from '~/utils/environment.utils';

export class MembershipController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    async fetchCurrUserAccount(logger: LoggerService) {
        const {
            userStore: { currUser },
            accountStore,
            userService,
            sessionCtrl,
            injectorService
        } = this.rootStore;

        let account = null;

        let forceFetchUser: ForceFetchUserFn;
        if (isReactFirst()) {
            forceFetchUser = sessionCtrl.forceFetchUser.bind(sessionCtrl);
        } else {
            const usersManager = injectorService?.get('UsersManager');
            forceFetchUser = usersManager.forceFetchCurrUser.bind(usersManager);
        }

        if (forceFetchUser) {
            try {
                const result = await forceFetchUser();
                const user: IUserResponse =
                    'data' in result ? result.data : result;
                account = user?.account || null;
            } catch (error) {
                logger.trackError({
                    error,
                    context: {
                        message:
                            'Error while calling provided forceFetchUser in MembershipController.fetchCurrUserAccount'
                    }
                });
            }
        }

        // If account is still null (injectorService path failed or wasn't taken)
        if (!account) {
            // Fallback to userService.getUser. Let errors propagate if this fails.
            const { account: fetchedAccount } = await userService.getUser(
                currUser._id
            );
            account = fetchedAccount;
        }

        if (!account) {
            // Log an error if account could not be fetched by either method
            const error = new Error(
                'Failed to fetch account from both UsersManager and userService'
            );
            logger.trackError({
                error,
                context: {
                    message:
                        'Account could not be fetched in MembershipController.fetchCurrUserAccount',
                    userId: currUser?._id // Add safe navigation in case currUser is not available
                }
            });
            throw error;
        }

        // Update stores with account data
        accountStore.update(account);
        currUser.setAccountId(account._id);

        return account;
    }

    purchaseSubscription(tokenId: string) {
        const {
            membershipUiStore: { selectedPlanCode, checkoutCouponCode }
        } = this.rootStore;

        const data = {
            plan_code: selectedPlanCode,
            coupon_code: checkoutCouponCode,
            token_id: tokenId
        };
        return this.rootStore.membershipService.purchaseSubscription(data);
    }

    _getBillingFormHelperText(errors?: IBillingFieldError[]) {
        return errors.map(errorItem => {
            const { field } = errorItem;

            const errorTranslator = {
                first_name: 'Enter your first name',
                last_name: 'Enter your last name',
                number: 'Enter a valid card number',
                month: 'Select month',
                year: 'Select year',
                cvv: 'Enter CVV',
                address1: 'Enter your address',
                country: 'Select a country',
                city: 'Enter a city',
                state: 'Select a state or province',
                postal_code: 'Enter your ZIP or postal'
            };
            errorItem.messages = [errorTranslator[field] || 'Invalid'];

            return errorItem;
        });
    }

    setBillingFormErrors(errors?: IBillingFieldError[]) {
        const { membershipUiStore } = this.rootStore;
        membershipUiStore.resetBillingFormErrors();
        const helperText = this._getBillingFormHelperText(errors);
        membershipUiStore.setBillingFormErrors(helperText);
    }

    setBillingFormMetaError(error) {
        const { membershipUiStore } = this.rootStore;
        membershipUiStore.setBillingFormMetaError(error);
    }

    async getReferralCode(userCouponCode: string) {
        if (!userCouponCode) {
            return;
        }

        const referralCode =
            await this.rootStore.membershipService.getReferralCode(
                userCouponCode
            );

        if (!referralCode?.code) {
            return;
        }

        return {
            code: referralCode.coupon,
            text: referralCode.code,
            referrerName: referralCode.code_owner?.full_name
        };
    }

    saveReferral(couponCode: string) {
        const {
            membershipService,
            membershipUiStore: { subscription },
            userStore: { currUser }
        } = this.rootStore;

        const shouldSaveReferral =
            !subscription &&
            couponCode !== currUser.referral_container?.pending_referral_code;

        if (!shouldSaveReferral) {
            return;
        }

        return membershipService.saveReferral({
            referral_code: couponCode,
            referred_id: currUser._id,
            source: 'promo_code'
        });
    }

    async previewPurchase(
        tokenId: string,
        previewWithCoupon: boolean
    ): Promise<IRecurlyPurchasePreviewResponse> {
        const {
            membershipService,
            membershipUiStore: { selectedPlanCode, checkoutCouponCode }
        } = this.rootStore;

        const response = await membershipService.previewPurchase({
            plan_code: selectedPlanCode,
            coupon_code: previewWithCoupon ? checkoutCouponCode : null,
            token_id: tokenId
        });
        return response;
    }

    @action.bound
    previewChange(previewWithCoupon: boolean) {
        const {
            membershipService,
            membershipUiStore: {
                selectedPlanCode,
                checkoutCouponCode,
                subscriptionCouponCode
            }
        } = this.rootStore;

        let couponCode: string;

        if (subscriptionCouponCode === checkoutCouponCode) {
            couponCode = null;
        } else {
            couponCode = previewWithCoupon ? checkoutCouponCode : null;
        }

        return membershipService.previewUpgrade({
            plan_code: selectedPlanCode,
            coupon_code: couponCode
        });
    }

    @action.bound
    async reactivateSubscription(
        modalStore: TModalStore,
        logger: LoggerService
    ) {
        const { membershipService, membershipUiStore } = this.rootStore;
        try {
            membershipUiStore.setIsSubmitButtonDisabled(true);
            await membershipService.reactivateSubscription();
            await new PollingSubscription(
                this.rootStore,
                logger
            ).initiatePolling('reactivate');
        } catch (error) {
            logger.trackError({
                error,
                context: {
                    message:
                        'Recurly timeout when trying to reactivate subscription'
                }
            });
        } finally {
            await this.getNextPayment();
            modalStore.closeGlobalModal('CancelChangeModalWrapper');
            membershipUiStore.setIsSubmitButtonDisabled(false);
        }
    }

    async getAndSavePlansAfterPriceChange() {
        const {
            membershipService,
            membershipUiStore: { setPlansAfterPriceUpdate }
        } = this.rootStore;

        try {
            const { plans_after_price_update } =
                await membershipService.getPlansForPriceUpdate();

            setPlansAfterPriceUpdate(plans_after_price_update);
        } catch (error) {
            trackError({
                error,
                message: 'Error while fetching plans after price update'
            });
        }
    }

    async changeSubscription(
        track: TTrackFunction,
        logger: LoggerService,
        intercomService: IIntercomService
    ) {
        const {
            membershipService,
            membershipUiStore,
            userStore: { currUser },
            companyStore: { currCompany },
            membershipUiStore: {
                selectedPlanCode,
                checkoutCouponCode,
                showBillingModal,
                setIsSubmitButtonDisabled,
                isChangeNextPlan,
                isDowngradedNextPlan,
                setIsDowngradedNextPlan,
                subscriptionCouponCode,
                subscription,
                setNextPlanCode
            },
            accountCtrl,
            companyCtrl
        } = this.rootStore;

        const isCancelChangeNextPlan =
            subscription?.plan_code === selectedPlanCode;

        try {
            setIsSubmitButtonDisabled(true);
            trackEvent('click');

            if (isCancelChangeNextPlan) {
                await membershipService.removeSubscriptionChange();
            } else {
                const shouldNotSendCouponCode =
                    checkoutCouponCode === subscriptionCouponCode;
                const couponCode = shouldNotSendCouponCode
                    ? ''
                    : checkoutCouponCode;

                await membershipService.changeSubscription({
                    plan_code: selectedPlanCode,
                    coupon_code: couponCode
                });

                // Refresh invoices
                this.getInvoiceHistory().then(invoiceHistoryResponse => {
                    membershipUiStore.setInvoiceHistory(invoiceHistoryResponse);
                });
            }

            const pollingType =
                !isChangeNextPlan && !isDowngradedNextPlan
                    ? 'upgrade'
                    : 'downgrade';
            await new PollingSubscription(
                this.rootStore,
                logger
            ).initiatePolling(pollingType);

            setNextPlanCode(selectedPlanCode);

            this.getNextPayment();

            companyCtrl.fetchCompany(currCompany._id, true);

            accountCtrl.fetchFeaturePlan({ forceFetch: true });

            await this.fetchCurrUserAccount(logger);

            intercomService.identify();

            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            isChangeNextPlan || isCancelChangeNextPlan
                ? showBillingModal(BillingModalType.ChangeNextPlanSuccess)
                : showBillingModal(BillingModalType.UpgradeSuccess);

            trackEvent('success');
        } catch (error) {
            trackEvent('error', error);
            logger.trackError({
                error,
                context: {
                    new_plan_code: selectedPlanCode,
                    message: 'Error when trying to change subscription'
                }
            });
            this.setBillingFormMetaError(
                error?.error?.message ||
                    error?.data?.error_message ||
                    'Some unexpected error has occurred, please try again later.'
            );
        } finally {
            setIsDowngradedNextPlan(
                isChangeNextPlan || isDowngradedNextPlan,
                selectedPlanCode
            );
            setIsSubmitButtonDisabled(false);
        }

        function trackEvent(eventAction: TAnalyticsEventAction, error?: any) {
            track(AnalyticsEvents.ChangeSubscription, {
                action: eventAction,
                curr_plan: currUser.account.subscription?.plan_code,
                new_plan: selectedPlanCode,
                ...(error ? { error } : {})
            });
        }
    }

    async removeSubscriptionChange(
        modalStore: TModalStore,
        logger: LoggerService
    ) {
        const { membershipService, membershipUiStore, userStore } =
            this.rootStore;
        try {
            membershipUiStore.setIsSubmitButtonDisabled(true);
            await membershipService.removeSubscriptionChange();
            await new PollingSubscription(
                this.rootStore,
                logger
            ).initiatePolling('cancel_downgrade');
        } catch (error) {
            logger.trackError({
                error,
                context: {
                    message: `Recurly timeout when trying to cancel downgrade`
                }
            });
        } finally {
            membershipUiStore.setIsDowngradedNextPlan(
                false,
                userStore.currUser.account?.subscription?.pricing_plan
                    ?.plan_type
            );
            modalStore.closeGlobalModal('CancelChangeModalWrapper');
            membershipUiStore.setIsSubmitButtonDisabled(false);
        }
    }

    async getBillingInfo() {
        const response =
            await this.rootStore.membershipService.getBillingInfo();
        return response;
    }

    async updateBillingInfo(tokenId: string) {
        const data = {
            token_id: tokenId
        };
        const response =
            await this.rootStore.membershipService.updateBillingInfo(data);
        return response;
    }

    updateNextPlanPeriod(defaultNextPlan: string) {
        const data = {
            selected_next_plan_code: defaultNextPlan
        };

        this.rootStore.membershipService.updateNextPlanPeriod(data).then(() => {
            const { userCtrl } = this.rootStore;
            userCtrl.updateAccountProperty(data);
        });
    }

    async resumeSubscription(logger: LoggerService) {
        const {
            membershipService,
            userStore: { currUser }
        } = this.rootStore;

        if (currUser.account.subscription.state === 'active') {
            await membershipService.cancelPauseSubscription();
        } else {
            await membershipService.resumePausedSubscription();
        }

        await this.fetchCurrUserAccount(logger);
    }

    async getInvoiceHistory(
        // default is last year
        begin_time = new Date(new Date().getFullYear() - 1, 0),
        end_time = new Date()
    ) {
        return this.rootStore.membershipService.getInvoiceHistory(
            begin_time,
            end_time
        );
    }

    async getNextPayment() {
        const {
            membershipUiStore: {
                checkIfNextPaymentIsDowngraded,
                setNextPayment
            }
        } = this.rootStore;

        const response =
            await this.rootStore.membershipService.getNextPayment();

        if (response?.length) {
            setNextPayment(response[0]?.attributes);
            checkIfNextPaymentIsDowngraded(response);
        }

        return response;
    }

    async getCoupon(couponCode: string, account_id?: string) {
        if (!couponCode) {
            return;
        }
        return this.rootStore.membershipService.getCoupon(
            couponCode,
            account_id
        );
    }

    async getBlockedCoupons() {
        const { membershipService } = this.rootStore;
        const blockedCoupons = await membershipService.getBlockedCoupons();
        this.rootStore.membershipUiStore.setBlockedCoupons(blockedCoupons);
    }

    async getIsEligibleForCoupon() {
        const { membershipService } = this.rootStore;
        const response = await membershipService.isEligibleForCoupon();
        this.rootStore.membershipUiStore.setIsEligibleForCoupon(!!response);
    }

    async downloadInvoicePdf(
        invoiceId: string,
        fileName: string
    ): Promise<void> {
        const {
            userStore: { currUser },
            globalContext: { clientPendingTaskCtrl }
        } = this.rootStore;
        const invoiceUrl = await clientPendingTaskCtrl.performClientPendingTask(
            {
                taskType: ClientPendingTaskType.GenerateInvoicePdf,
                taskData: {
                    invoice_id: invoiceId,
                    user_id: currUser._id,
                    file_name: fileName
                }
            }
        );

        const link = document.createElement('a');
        link.href = invoiceUrl;
        link.click();
    }

    @action.bound
    load(logger: LoggerService) {
        const {
            companyCtrl,
            membershipUiStore: { setIsMounted },
            companyStore: { currCompany }
        } = this.rootStore;
        setIsMounted(true);
        this.fetchCurrUserAccount(logger);
        companyCtrl.fetchCompany(currCompany._id);
    }

    async getReferrerData(couponCode: string) {
        const { membershipUiStore, membershipService } = this.rootStore;

        const coupon = await membershipService.getCoupon(couponCode);

        if (!coupon) {
            membershipUiStore.setIsCouponExistInServer(false);
            return;
        }

        const couponData = { name: '', amount: '' };

        const referrerFullName = getReferrerFullName(coupon?.referrer);
        if (referrerFullName) {
            couponData.name = referrerFullName;
        }

        const discountAmount =
            coupon.discount_amount || coupon.discount_percent;
        const discountType = coupon.discount_amount ? 'fixed' : 'percent';
        couponData.amount = getDiscountAmountWithUnit(
            discountAmount,
            discountType
        );

        return couponData;
    }

    @action.bound
    async checkPicTimeCouponEligibility() {
        const { membershipService, membershipUiStore } = this.rootStore;
        const response =
            await membershipService.checkPicTimeCouponEligibility();

        membershipUiStore.setIsEligibleForPicTimeCoupon(response);

        // We remove association to referrals if the user is eligible for Pic-Time coupon
        if (response.eligible) {
            const {
                userStore: { currUser }
            } = this.rootStore;
            currUser?.clearPendingReferralContainer();
            deleteStoredCouponCode();
        }

        return { eligible: response.eligible, source: response.source };
    }
}
